import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';

/* 2026-09-17 (local search build): the three exact-match service pages
   joined the Reception dropdown so they get a sitewide internal link from
   every page. The Phoenix location page and the comparison page are linked
   from the footer and from the pages themselves instead, to keep this
   menu readable. */
const receptionLinks = [{
  to: '/what-it-fixes',
  label: 'What it fixes'
}, {
  to: '/how-it-works',
  label: 'How it works'
}, {
  to: '/answering-service-for-contractors',
  label: 'Answering service for contractors'
}, {
  to: '/missed-call-text-back',
  label: 'Missed-call text-back'
}, {
  to: '/crm-for-contractors',
  label: 'CRM for contractors'
}, {
  to: '/getting-started',
  label: 'Getting started'
}, {
  to: '/pricing#viridia-system',
  label: 'Pricing for The Viridia System'
}];

/* 2026-09-15 (Kevin, three-services restructure): nav rebuilt around three
   doors instead of one -- Reception (the Viridia System: everything a
   front-office receptionist would do), Yelp Ads, Websites. Kevin chose
   "Reception" over "Business Tools" on 2026-09-15; "Virtual Reception"
   was rejected because it invites comparison to call-answering services.
   Order and the seven-item cap are both from the handoff. */
const links = [{
  to: '/',
  label: 'Home'
}, {
  to: '/yelp-ads',
  label: 'Yelp Ads'
}, {
  to: '/websites',
  label: 'Websites'
}, {
  to: '/why',
  label: 'Why Viridia'
}, {
  to: '/contact',
  label: 'Contact'
}];
export function Nav() {
  const [open, setOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const location = useLocation();
  const toolsActive = receptionLinks.some(l => location.pathname === l.to.split('#')[0]);
  return <header className="sticky top-0 z-50 border-b border-[#1a1a1a] bg-[#0a0a0a]/85 backdrop-blur" data-id="element-483" data-mp="2vtffgrf7b30f">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-4" data-id="element-484" data-mp="tvwtykb419zk">
        <Link to="/" className="flex items-center gap-2.5" onClick={() => setOpen(false)} data-id="element-485" data-mp="xya37ip2fq13">
          <img src="https://cdn.magicpatterns.com/uploads/dBgonnHgNocdZqWCx2rT29/VA-Logo_(1).png" alt="Viridia Analytics logo" className="h-8 w-8 object-contain" data-id="element-486" data-mp="k6vh32zv38pt" />
          <span className="text-sm font-semibold tracking-tight text-white" data-id="element-487" data-mp="a7wh08t5wlef">
            Viridia Analytics
          </span>
        </Link>

        <nav aria-label="Main" className="hidden items-center gap-7 md:flex" data-id="element-488" data-mp="f8k62oxtys60">
          <NavLink to="/" end className={({
          isActive
        }) => `text-sm transition-colors ${isActive ? 'text-white' : 'text-neutral-400 hover:text-white'}`} data-id="element-489" data-mp="18sx52m681yv">
            Home
          </NavLink>

          <div className="relative" onMouseEnter={() => setToolsOpen(true)} onMouseLeave={() => setToolsOpen(false)} data-id="element-490" data-mp="20kbyav18g5xz">
            <button type="button" onClick={() => setToolsOpen(v => !v)} aria-expanded={toolsOpen} className={`flex items-center gap-1 text-sm transition-colors ${toolsActive ? 'text-white' : 'text-neutral-400 hover:text-white'}`} data-id="element-491" data-mp="2amfwlccv9ep9">
              Reception
              <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${toolsOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-492" data-mp="2tdyyffv5mmpp" />
            </button>
            {toolsOpen && <div className="absolute left-0 top-full w-72 pt-2" data-id="element-493" data-mp="32ex19utuwo6p">
                <div className="overflow-hidden rounded-xl border border-[#1c1c1c] bg-[#0d0d0d] py-2 shadow-xl" data-id="element-494" data-mp="2yf65tdtdxjvu">
                  {receptionLinks.map(l => <Link key={l.to} to={l.to} className="block px-4 py-2.5 text-sm text-neutral-300 transition-colors hover:bg-[#151515] hover:text-white" data-id="element-495" data-mp="1403lduj70rzs">
                      {l.label}
                    </Link>)}
                </div>
              </div>}
          </div>

          {links.slice(1).map(l => <NavLink key={l.to} to={l.to} className={({
          isActive
        }) => `text-sm transition-colors ${isActive ? 'text-white' : 'text-neutral-400 hover:text-white'}`} data-id="element-496" data-mp="1jfz4j946pogb">
              {l.label}
            </NavLink>)}
        </nav>

        <div className="hidden md:block" data-id="element-497" data-mp="vpwipaqv5eq5">
          <Link to="/contact" className="rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-[#0a0a0a] transition-colors hover:bg-emerald-400" data-id="element-498" data-mp="10hwagfws51bn">
            Book a 15-min call
          </Link>
        </div>

        <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-label={open ? 'Close menu' : 'Open menu'} className="rounded-md p-2 text-neutral-300 md:hidden" data-id="element-499" data-mp="80aeu9zjqbbt">
          {open ? <XIcon className="h-5 w-5" data-id="element-500" data-mp="3mp13al09g14u" /> : <MenuIcon className="h-5 w-5" data-id="element-501" data-mp="3mo96j6uxfxcz" />}
        </button>
      </div>

      {open && <nav aria-label="Mobile" className="border-t border-[#1a1a1a] px-6 pb-6 pt-4 md:hidden" data-id="element-502" data-mp="3imxih5rpbk6w">
          <ul className="flex flex-col gap-1" data-id="element-503" data-mp="3dpxinclg3do8">
            <li data-id="element-504" data-mp="3dqp805og8f62">
              <NavLink to="/" end onClick={() => setOpen(false)} className={({
            isActive
          }) => `block py-2.5 text-base ${isActive ? 'text-white' : 'text-neutral-300'}`} data-id="element-505" data-mp="38yfi2pnc3f3s">
                Home
              </NavLink>
            </li>

            <li data-id="element-506" data-mp="2uxv01vp8tnbc">
              <button type="button" onClick={() => setMobileToolsOpen(v => !v)} aria-expanded={mobileToolsOpen} className="flex w-full items-center justify-between py-2.5 text-base text-neutral-300" data-id="element-507" data-mp="2v0h77u8820t1">
                Reception
                <ChevronDownIcon className={`h-4 w-4 transition-transform ${mobileToolsOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-508" data-mp="2kb6ivpl7o5ha" />
              </button>
              {mobileToolsOpen && <ul className="ml-3 space-y-1 border-l border-[#1c1c1c] pl-4" data-id="element-509" data-mp="2z1xb2yqpt6kx">
                  {receptionLinks.map(l => <li key={l.to} data-id="element-510" data-mp="340qjwdv249er">
                      <Link to={l.to} onClick={() => setOpen(false)} className="block py-2 text-sm text-neutral-400" data-id="element-511" data-mp="2pcdnjfs24ojq">
                        {l.label}
                      </Link>
                    </li>)}
                </ul>}
            </li>

            {links.slice(1).map(l => <li key={l.to} data-id="element-512" data-mp="1bc95gpterlv3">
                <NavLink to={l.to} onClick={() => setOpen(false)} className={({
            isActive
          }) => `block py-2.5 text-base ${isActive ? 'text-white' : 'text-neutral-300'}`} data-id="element-513" data-mp="1bbhke8xft6j2">
                  {l.label}
                </NavLink>
              </li>)}

            <li data-id="element-514" data-mp="sdepxem4ehdv">
              <Link to="/contact" onClick={() => setOpen(false)} className="mt-3 inline-block rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-[#0a0a0a]" data-id="element-515" data-mp="sey7d8jiiyh8">
                Book a 15-min call
              </Link>
            </li>
          </ul>
        </nav>}
    </header>;
}